/// <amd-module name="VSS/Platform/Identity" />
import { IVssPageContext, IVssService, Services, VssService } from "./Context";
import { IdentityRef } from "./Contracts/WebApi";
import { IVssContributionService } from "./Contribution";
import { IVssPageService } from "./Page";
import { combineUrlPaths, Uri } from "./Util/Url";

/**
 * Details for a VSS identity that was seeded using the IClientIdentityDetailsProviderService
 */
export interface IIdentityDetails {
    /**
     * VSID of the identity
     */
    id: string;

    /**
     * Display name of the user/group
     */
    displayName: string;

    /**
     * Url for the identity's avatar/image
     */
    imageUrl: string;

    /**
     * Unique id for the identity's avatar/image
     */
    imageId: string;
}

/**
 * Sizes for identity image URLs
 */
export const enum IdentityImageSize {
    /**
     * 34x34
     */
    small = 0,

    /**
     * 44x44
     */
    medium = 1,

    /**
     * 220x220
     */
    large = 2
}

/**
 * Service for obtaining identity information for users/groups populated by the IClientIdentityDetailsProviderService service.
 */
export interface IVssIdentityService extends IVssService {
    /**
     * Gets identity information for the specified user.
     *
     * @param id VSID of the user to fetch details for
     */
    getIdentityDetails(id: string): IIdentityDetails;

    /**
     * Gets the url to an identity's avatar/image
     *
     * @param identityId IdentityRef for the identity (preferred) or the VSID of the user to fetch the image url for
     * @param size Optional size for the resulting identity image
     */
    getIdentityImageUrl(identityId: IdentityRef | string, size?: IdentityImageSize): string;
}

const identityDetailsSharedDataKey = "_identityDetails";
const gitHubIdentityHostPath = "avatars.githubusercontent.com";

interface ISharedIdentityDetails {
    displayName: string;
    identityImageId: string;
}

class VssIdentityService extends VssService implements IVssIdentityService {
    private cachedIdentityDetails: { [id: string]: IIdentityDetails } = {};

    public getIdentityDetails(id: string): IIdentityDetails {
        let identityDetails = this.cachedIdentityDetails[id];

        if (!identityDetails) {
            const details = this.getSharedIdentityDetails(id);
            if (details) {
                identityDetails = {
                    id,
                    displayName: details.displayName,
                    imageId: details.identityImageId,
                    imageUrl: this.getIdentityImageUrl(id)
                };

                this.cachedIdentityDetails[id] = identityDetails;
            }
        }

        if (!identityDetails) {
            throw new Error(`Missing shared identity details for identity with id ${id}.`);
        }

        return identityDetails;
    }

    public getIdentityImageUrl(identity: IdentityRef | string, size: IdentityImageSize = IdentityImageSize.small): string {
        if (typeof identity === "string") {
            return this._getIdentityImageUrlFromIdentityId(identity, size);
        } else {
            return this._getIdentityImageUrlFromIdentityRef(identity, size);
        }
    }

    private getSharedIdentityDetails(id: string): ISharedIdentityDetails | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const sharedIdentityDetails = contributionService.getSharedData<{ [id: string]: ISharedIdentityDetails }>(identityDetailsSharedDataKey);
        return sharedIdentityDetails ? sharedIdentityDetails[id] : undefined;
    }

    /**
     * Returns a URL to an identity image. Uses the old endpoint. Prefer using _getIdentityImageurlFromIdentityRef().
     * @param identityId
     * @param size
     */
    private _getIdentityImageUrlFromIdentityId(identityId: string, size: IdentityImageSize): string {
        let identityImageId: string | undefined;

        const identityDetails = this.getSharedIdentityDetails(identityId);
        if (identityDetails) {
            // If we have an identity details object, then the identity image id was looked-up. If
            // the user doesn't have an explicit image, it will be undefined here, but we can send
            // a token in this place (0) to allow this image to be cached indefinitely. It will not
            // change until the user updates his image, in which case there will be a non-empty image id.
            identityImageId = identityDetails.identityImageId || "0";
        }

        let url = `_api/_common/identityImage?id=${identityId}&size=${size}`;
        if (identityImageId) {
            url += `&t=${identityImageId}`;
        }

        const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData();
        if (pageData && pageData.hostPath) {
            url = combineUrlPaths(encodeURI(pageData.hostPath), url);
        }

        return url;
    }

    /**
     * Extracts identity image URL from an identityRef.
     * @param identityRef
     */
    private _getIdentityImageUrlFromIdentityRef(identityRef: IdentityRef, size: IdentityImageSize): string {
        const hasAvatarHref = identityRef._links && identityRef._links.avatar && identityRef._links.avatar.href;
        const isGitHubIdentityImage = hasAvatarHref && (identityRef._links.avatar.href as string).indexOf(gitHubIdentityHostPath) !== -1;

        // Github avatars use size for the pixel dimensions of the avatar image.
        if (isGitHubIdentityImage) {
            const url = new Uri(identityRef._links.avatar.href);
            let gitHubSize;
            switch (size) {
                case 1:
                    gitHubSize = 44;
                    break;
                case 2:
                    gitHubSize = 220;
                    break;
                default:
                    gitHubSize = 34;
            }

            url.addQueryParam("size", "" + gitHubSize, /*replaceExisting*/ true);
            return url.absoluteUri;
        } else if (hasAvatarHref) {
            const url = new Uri(identityRef._links.avatar.href);
            url.addQueryParam("size", "" + size, /*replaceExisting*/ true);
            return url.absoluteUri;
        } else if (identityRef.descriptor) {
            // If no avatar url, create one with descriptor.
            return this._getIdentityAvatarUrl(identityRef.descriptor);
        } else {
            // No avatar URL or descriptor provided, fall back to old style
            return identityRef.imageUrl || this._getIdentityImageUrlFromIdentityId(identityRef.id, size);
        }
    }

    private _getIdentityAvatarUrl(descriptor: string): string {
        // URL pattern /_apis/GraphProfile/MemberAvatars/{memberDescriptor}
        let url = `_apis/GraphProfile/MemberAvatars/${descriptor}`;
        const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData();
        if (pageData && pageData.hostPath) {
            url = combineUrlPaths(encodeURI(pageData.hostPath), url);
        }
        return url;
    }
}

Services.add("IVssIdentityService", { serviceFactory: VssIdentityService });

/** Syntactic-sugar methods for convenient identity-related lookups */

/**
 * Gets the url to an identity's avatar/image
 *
 * @param identityId IdentityRef for the identity (preferred) or the VSID of the user to fetch the image url for
 * @param size Optional size for the resulting identity image
 */

export function getIdentityImageUrl(pageContext: IVssPageContext, identity: IdentityRef | string, size?: IdentityImageSize): string {
    const identityService = pageContext.getService<IVssIdentityService>("IVssIdentityService");
    return identityService.getIdentityImageUrl(identity, size);
}
